import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useMap } from "react-map-gl";
import { useContextMenuStore, useDrawMapStore, usePipedriveStore, usePropertyStore } from "../..";
import useRowClassName from "../../hooks/useRowClassName";
import { DealStatus, IDealState } from "../../interfaces/IDeal";
import IEntityFieldsProps from "../../interfaces/IEntityFieldsProps";
import GeometryService from "../../services/geometryService";
import { dealLostReasons } from "../../utils/dealLostReasons";
import { dealStatusToString } from "../../utils/enumUtils";
import commonStyles from "../Common.module.css";
import editStyles from "../Edit.module.css";
import AddressInput from "../inputs/AddressInput";
import DealParcelsInput from "../inputs/DealParcelsInput";
import NumberInput from "../inputs/NumberInput";
import PipedriveCompanyInput from "../inputs/PipedriveCompanyInput";
import PipedriveLabelInput from "../inputs/PipedriveLabelInput";
import PipedrivePersonInput from "../inputs/PipedrivePersonInput";
import SelectInput from "../inputs/SelectInput";
import StringInput from "../inputs/StringInput";
import TextInput from "../inputs/TextInput";
import MarkAsLostModal from "./MarkAsLostModal";

function DealFields({ formik, children, onMarkAsLost }: IDealFieldsProps) {
    const map = useMap().map!;
    const pipedriveStore = usePipedriveStore();
    const drawMapStore = useDrawMapStore();
    const contextMenuStore = useContextMenuStore();
    const propertyStore = usePropertyStore();
    const geometryService = useMemo(() => new GeometryService(), []);
    const rowClassName = useRowClassName();
    const [isMarkAsLostModalOpen, setIsMarkAsLostModalOpen] = useState(false);
    const [statusBeforeLost, setStatusBeforeLost] = useState<DealStatus | null>(null);

    const onStatusChange = (status: string | null) => {
        if (status === DealStatus.Lost && formik.values.status !== DealStatus.Lost) {
            setStatusBeforeLost(formik.values.status);
            setIsMarkAsLostModalOpen(true);
            return {};
        }

        if (status !== DealStatus.Lost)
            return { lostReason: null, lostComment: null };

        return {};
    };

    const cancelMarkAsLost = () => {
        formik.setValues({ ...formik.values, status: statusBeforeLost, lostReason: null, lostComment: null });
        setIsMarkAsLostModalOpen(false);
    };

    const markAsLost = async (lostReason: string, comment: string | null) => {
        const values = { ...formik.values, status: DealStatus.Lost, lostReason, lostComment: comment };
        formik.setValues(values);
        await onMarkAsLost?.(values);
        setIsMarkAsLostModalOpen(false);
    };

    useEffect(() => {
        contextMenuStore.setOnAddParcel(async (coordinates) => {
            const [parcel] = await propertyStore.fetchParcels([coordinates], map);
            if (!parcel) return;

            drawMapStore.setResult({
                ...drawMapStore.result!,
                features: [
                    ...drawMapStore.result!.features,
                    ...geometryService.mapIShapesToPolygonFeatures([parcel.shape]),
                ],
            });

            formik.setFieldValue("dealShapeApns", [
                ...formik.values.dealShapeApns,
                parcel.parcelId,
            ]);
        }, true);

        formik.setFieldValue("parcelId", formik.values.dealShapeApns.filter(v => !!v).join(", "));

        return () => contextMenuStore.clearOnAddParcel();
    }, [formik.values.dealShapeApns]);

    return (
        <>
        <div className={editStyles.container}>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Deal</div>
                <div className={rowClassName}>
                    <AddressInput formik={formik} label="FULL ADDRESS" name="address" />
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="NAME" name="name" />
                </div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="STAGE" name="dealStageId" valueAsCorrectType={value => Number(value)}>
                        {pipedriveStore.dealStages.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </SelectInput>
                    <NumberInput formik={formik} label="VALUE ($)" name="value" />
                    <NumberInput formik={formik} label="ASKING PRICE ($)" name="askingPrice" />
                </div>
                <div className={rowClassName}>
                    <NumberInput formik={formik} label="BUILDING SIZE (SQ-FT)" name="buildingSize" />
                    <NumberInput formik={formik} label="LAND SIZE (ACRES)" name="landSizeAcres" />
                    <NumberInput formik={formik} label="USABLE LAND (ACRES)" name="usableLand" />
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="ZONING" name="zoning" />
                </div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="STATUS" name="status" valueAsCorrectType={value => value as DealStatus} onChange={onStatusChange}>
                        <option value={DealStatus.Open}>{dealStatusToString(DealStatus.Open)}</option>
                        <option value={DealStatus.Won}>{dealStatusToString(DealStatus.Won)}</option>
                        <option value={DealStatus.Lost}>{dealStatusToString(DealStatus.Lost)}</option>
                        <option value={DealStatus.Deleted}>{dealStatusToString(DealStatus.Deleted)}</option>
                    </SelectInput>
                </div>
                {formik.values.status === DealStatus.Lost && (
                    <>
                        <div className={rowClassName}>
                            <SelectInput formik={formik} label="LOST REASON" name="lostReason" valueAsCorrectType={value => value}>
                                <option value="">----</option>
                                {dealLostReasons.map(reason => (
                                    <option key={reason} value={reason}>{reason}</option>
                                ))}
                            </SelectInput>
                        </div>
                        <div className={rowClassName}>
                            <TextInput formik={formik} label="LOST COMMENT" name="lostComment" placeholder="Add any additional context..." />
                        </div>
                    </>
                )}
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="NON-FUND DEAL" name="nonFund" valueAsCorrectType={value => value}>
                        {pipedriveStore.nonFundOptions.map(o => (
                            <option key={o.id} value={o.label}>{o.label}</option>
                        ))}
                    </SelectInput>
                </div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="DEAL PROFILE" name="dealProfile" valueAsCorrectType={value => value}>
                        {pipedriveStore.profileOptions.map(o => (
                            <option key={o.id} value={o.label}>{o.label}</option>
                        ))}
                    </SelectInput>
                </div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="SOURCING METHOD" name="sourcingMethod" valueAsCorrectType={value => value}>
                        <option value={""}>----</option>
                        {pipedriveStore.sourcingMethodOptions.map(o => (
                            <option key={o.id} value={o.label}>{o.label}</option>
                        ))}
                    </SelectInput>
                    <StringInput formik={formik} label="OID" name="OID" />
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="PARCEL ID" name="parcelId" />
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Location</div>
                <div className={rowClassName}>
                    <div className={editStyles.doubleWidthRowGroup}>
                        <div className={rowClassName}>
                            <StringInput formik={formik} label="CITY" name="city" />
                            <StringInput formik={formik} label="STATE" name="state" />
                        </div>
                        <div className={rowClassName}>
                            <StringInput formik={formik} label="COUNTY" name="county" />
                        </div>
                    </div>
                    <div className={editStyles.rowGroup}>
                        <div className={rowClassName}>
                            <DealParcelsInput formik={formik} label="PARCELS" name="dealShapeApns" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Contacts</div>
                <div className={rowClassName}>
                    <PipedrivePersonInput formik={formik} label="PROPERTY OWNER" name="propertyOwnerName" />
                    <PipedrivePersonInput formik={formik} label="BROKER" name="brokerName" />
                </div>
                <div className={rowClassName}>
                    <PipedrivePersonInput formik={formik} label="CONTACT NAME" name="contactPersonName" />
                    <PipedriveCompanyInput formik={formik} label="CONTACT COMPANY" name="contactCompanyName" />
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="TENANT" name="tenant" />
                </div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="ASSIGNED TO" name="assignedToUserId" valueAsCorrectType={value => Number(value)}>
                        {pipedriveStore.users.map(u => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                    </SelectInput>
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Internal</div>
                <div className={rowClassName}>
                    <PipedriveLabelInput formik={formik} label="LABEL" name="labelId" />
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="DROPBOX LINK" name="dropboxLink" />
                </div>
                {children}
            </div>
        </div>
        {isMarkAsLostModalOpen && (
            <MarkAsLostModal
                lostReason={formik.values.lostReason}
                comment={formik.values.lostComment ?? null}
                onCancel={cancelMarkAsLost}
                onMarkAsLost={markAsLost}
            />
        )}
        </>
    );
}

export default observer(DealFields);

interface IDealFieldsProps extends IEntityFieldsProps<IDealState> {
    onMarkAsLost?: (values: IDealState) => Promise<void>;
}
